/**
 * Shared chart theme for the analytics dashboards.
 *
 * One palette, one font stack, one grid/axis/tooltip config. Everything under
 * views/analytics and components/analytics imports from here; nothing declares
 * a hex inline. This file is what makes twelve charts read as one dashboard -
 * QUMGraph.vue and DemoGraphs.vue each reinvented their own #666 titles and
 * ad-hoc colours, and it shows.
 *
 * The palette is the validated one from the mockup, extended to its full eight
 * documented slots (worst adjacent colour-blind dE 9.1, worst adjacent
 * normal-vision dE 19.6, both against the white card surface). Copy values,
 * don't re-derive them, and re-run the validator if a slot ever changes: five
 * slots was one short of what /analytics/register-movements actually returns,
 * so `administration` and `clinician bag checkout` both rendered blue. Past
 * eight series, fold - see foldSeries.
 *
 * Light mode only ships - the app has no dark-mode mechanism. Three light
 * series sit below 3:1 contrast on white, which is legal only with a relief
 * channel: that is why every ChartCard carries a Table toggle. Keep it.
 */

export const SURFACE = '#ffffff';

export const SERIES = [
    '#2a78d6', // blue
    '#eb6834', // orange
    '#1baf7a', // aqua
    '#eda100', // yellow
    '#e87ba4', // magenta
    '#008300', // green
    '#4a3aa7', // violet
    '#e34948', // red
];

/**
 * Reserved for the folded tail only (see foldSeries). Grey because "Other" is
 * not an identity - it must not read as a ninth category.
 */
export const SERIES_OTHER = '#9aa0a6';

export const RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#104281'];
export const ORDINAL = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];
export const DIVERGING = { pos: '#2a78d6', mid: '#d8dce0', neg: '#d03b3b' };
export const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' };

/**
 * Tile deltas. A tinted pill rather than bare coloured text: the arrow plus the
 * wash carry the direction, so the reading never rests on hue alone. The green
 * is the darker success-text step - STATUS.good is a mark colour and sits at
 * 3.3:1 on white, under the 4.5:1 a 12px figure needs.
 */
export const DELTA = {
    good: { color: '#006300', background: 'rgba(0, 99, 0, 0.08)' },
    bad: { color: '#d03b3b', background: 'rgba(208, 59, 59, 0.08)' },
    flat: { color: '#7a7f85', background: 'rgba(122, 127, 133, 0.1)' },
};

/**
 * Which way is good depends on the metric, not the sign: a falling discrepancy
 * rate is an improvement, a falling stocktake compliance is not.
 */
export function deltaTone(delta, downIsGood = false) {
    if (!delta) return DELTA.flat;
    const improving = downIsGood ? delta < 0 : delta > 0;
    return improving ? DELTA.good : DELTA.bad;
}

// Avenir first, matching main.scss - the charts were the only thing in the app
// still opening on Helvetica, and at 11px axis text the difference shows.
export const FONT_STACK = 'Avenir, Helvetica, Arial, sans-serif';
export const TEXT = '#333333';
export const TEXT_MUTED = '#7a7f85';
export const GRID_LINE = '#eceef0';
/** One step darker than the grid: for the zero rule, which is not a gridline. */
export const BASELINE = '#d3d8dc';

/**
 * Axis and label numbers. Apex's default prints raw floats, which gave the
 * movement counts a "320.00" axis; rounding everything to integers instead
 * would flatten the median-hours panels to whole numbers. So: integers stay
 * integers, fractions keep one decimal.
 */
export function formatNumber(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    // The same formatter sits on both axes, and on a heatmap or a horizontal
    // bar the y-axis carries category labels rather than measures - pass those
    // through untouched instead of trying to round a drug name.
    if (typeof value !== 'number') return value;
    if (Number.isInteger(value)) return value.toLocaleString();
    return Number(value.toFixed(1)).toLocaleString();
}

export function formatPercent(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    if (typeof value !== 'number') return value;
    return `${formatNumber(value)}%`;
}

/**
 * Fold a series list down to the palette's width, summing the tail into
 * "Other". The alternative is Apex cycling back to slot 1, which silently
 * gives two series the same colour. Returns the colours to use alongside, so
 * the folded series always lands on grey rather than on slot 8's red.
 */
export function foldSeries(series, limit = SERIES.length) {
    if (!Array.isArray(series) || series.length === 0) return { series: [], colors: SERIES };
    if (series.length <= limit) return { series, colors: SERIES.slice(0, series.length) };

    const kept = series.slice(0, limit - 1);
    const tail = series.slice(limit - 1);
    const length = Math.max(...tail.map((s) => (s.data || []).length));
    const data = Array.from({ length }, (_, i) =>
        tail.reduce((sum, s) => sum + (typeof (s.data || [])[i] === 'number' ? s.data[i] : 0), 0)
    );

    return {
        series: [...kept, { name: 'Other', data }],
        colors: [...SERIES.slice(0, limit - 1), SERIES_OTHER],
    };
}

/** Base options every chart shares. Spread first, then override. */
export function baseOptions() {
    return {
        chart: {
            fontFamily: FONT_STACK,
            foreColor: TEXT_MUTED,
            toolbar: { show: false },
            // Panels draw in left-to-right order rather than all at once. It is
            // the one bit of motion here; keep it short enough that a refetch
            // never feels like a reload.
            animations: {
                enabled: true,
                easing: 'easeout',
                speed: 400,
                animateGradually: { enabled: true, delay: 60 },
                dynamicAnimation: { enabled: true, speed: 250 },
            },
        },
        colors: SERIES,
        grid: {
            borderColor: GRID_LINE,
            strokeDashArray: 0,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            // Room for the direct labels that ride outside a mark's end.
            padding: { top: 8, right: 16, bottom: 0, left: 8 },
        },
        dataLabels: { enabled: false },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            fontSize: '12px',
            offsetY: 2,
            itemMargin: { horizontal: 8, vertical: 2 },
            markers: { width: 9, height: 9, radius: 5, offsetX: -3 },
        },
        states: {
            hover: { filter: { type: 'lighten', value: 0.08 } },
            active: { filter: { type: 'none' } },
        },
        tooltip: {
            theme: 'light',
            shared: true,
            intersect: false,
            style: { fontSize: '12px', fontFamily: FONT_STACK },
            y: { formatter: formatNumber },
        },
        xaxis: {
            axisBorder: { color: GRID_LINE },
            axisTicks: { color: GRID_LINE },
            labels: { rotate: 0, hideOverlappingLabels: true, trim: false, style: { fontSize: '11px' } },
        },
        yaxis: { labels: { formatter: formatNumber, style: { fontSize: '11px' } } },
    };
}

/**
 * Turn a date-categorised chart into a real datetime axis.
 *
 * The panels ship ISO dates as string categories, so Apex had to label every
 * one of them - thirty diagonal labels under four separate panels. On a
 * datetime axis it thins them itself and they sit level.
 */
export function withDateAxis(options) {
    return {
        ...options,
        xaxis: {
            ...options.xaxis,
            type: 'datetime',
            // The floating axis tooltip duplicates what the shared tooltip
            // already says, and it lands on top of the bars.
            tooltip: { enabled: false },
            labels: {
                ...options.xaxis.labels,
                datetimeUTC: false,
                datetimeFormatter: { year: 'yyyy', month: "MMM 'yy", day: 'd MMM', hour: 'HH:mm' },
            },
        },
        tooltip: { ...options.tooltip, x: { format: 'ddd d MMM' } },
    };
}

/**
 * Drop the legend box. For a single series it restates the card title with a
 * swatch in front of it and costs a line of height.
 */
export function withoutLegend(options) {
    return { ...options, legend: { ...options.legend, show: false } };
}

/**
 * Clamp a value axis to 0-100 and suffix it. A share panel drawn on Apex's
 * auto scale ran to 120%, which is not a thing.
 */
export function withPercentAxis(options, { horizontal = false } = {}) {
    const axis = horizontal ? 'xaxis' : 'yaxis';

    return {
        ...options,
        [axis]: {
            ...options[axis],
            min: 0,
            max: 100,
            labels: { ...(options[axis].labels || {}), formatter: formatPercent },
        },
        tooltip: { ...options.tooltip, y: { formatter: formatPercent } },
    };
}

/**
 * Force whole-number ticks. Apex divides the range by its default tick count,
 * so an alert count topping out at 2 was labelled 0.0 / 0.7 / 1.3 / 2.0.
 */
export function withIntegerAxis(options, maxValue) {
    const max = Math.max(1, Math.ceil(maxValue || 1));

    return {
        ...options,
        yaxis: { ...options.yaxis, min: 0, max, tickAmount: Math.min(max, 4) },
    };
}

/**
 * A value printed at the data end, outside the mark, in text ink - the axis
 * numbers then come off, since the label carries them. Outside rather than
 * inside because several series fills are too light to take white text.
 */
export function endLabels({ formatter = formatNumber, horizontal = false, series, labelZero = false } = {}) {
    return {
        dataLabels: {
            enabled: true,
            ...(series ? { enabledOnSeries: series } : {}),
            // Zeros are dropped by default - a 30-day count series is mostly
            // zeros and labelling each one buries the values that matter. On a
            // short category list, pass labelZero: a labelled 0 reads as
            // measured, an empty row reads as missing data.
            formatter: (value) => {
                if (value === null) return '';
                if (value === 0 && !labelZero) return '';
                return formatter(value);
            },
            offsetX: horizontal ? 22 : 0,
            offsetY: horizontal ? 0 : -6,
            style: { fontSize: '11px', fontWeight: 600, colors: [TEXT] },
            background: { enabled: false },
        },
    };
}

export function barOptions({ stacked = false, horizontal = false, stackType, distributed = false } = {}) {
    const base = baseOptions();

    return {
        ...base,
        chart: { ...base.chart, type: 'bar', stacked, ...(stackType ? { stackType } : {}) },
        plotOptions: {
            bar: {
                horizontal,
                distributed,
                // Rounded only at the data end - a radius on the baseline end
                // lifts the bar off its own axis.
                borderRadius: 4,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
                // 70% filled the slot; the leftover is what makes a column
                // chart look considered rather than packed.
                columnWidth: '45%',
                barHeight: '55%',
                // Where a direct label lands when one is switched on: the cap
                // of a column, the tip of a bar. Inert while dataLabels are
                // off, which is the default.
                dataLabels: { position: 'top' },
            },
        },
        // Apex fills bars at 0.85 opacity by default, which washes every
        // series a step lighter than the palette it was validated as - the
        // three already-marginal slots worst of all. Render them solid.
        fill: { opacity: 1 },
        // The 2px surface gap that separates touching fills. White doing the
        // work, so no border ink is added to the marks.
        ...(stacked ? { stroke: { show: true, width: 2, colors: [SURFACE] } } : {}),
        ...(distributed ? { legend: { ...base.legend, show: false } } : {}),
    };
}

/**
 * Ordered stages (request -> supply -> ready -> pickup) are ordinal, not
 * nominal, so the ramp is legitimate here: the reader sees the sequence in the
 * colour. Never do this to product or drug names.
 */
export function ordinalBarOptions() {
    const options = barOptions({ distributed: true });

    return {
        ...options,
        colors: ORDINAL,
        ...endLabels(),
        yaxis: { ...options.yaxis, labels: { ...options.yaxis.labels, show: false } },
        grid: { ...options.grid, yaxis: { lines: { show: false } } },
    };
}

/**
 * Value-banded horizontal bars: the fill carries state, not identity, so it
 * takes status tokens rather than series slots. Bands are [{ upTo, color }],
 * lowest first; the last band catches everything above.
 */
export function bandedBarOptions(bands) {
    const options = barOptions({ horizontal: true });
    let floor = -1e9;

    return {
        ...options,
        colors: [bands[0].color],
        plotOptions: {
            ...options.plotOptions,
            bar: {
                ...options.plotOptions.bar,
                colors: {
                    ranges: bands.map(({ upTo, color }) => {
                        const range = { from: floor, to: upTo === null ? 1e9 : upTo, color };
                        floor = upTo;
                        return range;
                    }),
                },
            },
        },
    };
}

/**
 * Diverging column chart (the variance panel): positive one hue, negative
 * another - sign carries meaning, so it must not ride on a single series
 * colour. The zero rule is drawn explicitly; on a mostly-zero series the
 * reader otherwise has to infer where zero is from the tick labels.
 */
export function divergingBarOptions() {
    const options = barOptions();

    return {
        ...options,
        colors: [DIVERGING.pos],
        plotOptions: {
            ...options.plotOptions,
            bar: { ...options.plotOptions.bar, colors: { ranges: [{ from: -1e9, to: -0.000001, color: DIVERGING.neg }] } },
        },
        annotations: {
            yaxis: [{ y: 0, borderColor: BASELINE, borderWidth: 1, strokeDashArray: 0 }],
        },
    };
}

/**
 * @param {object} [opts]
 * @param {boolean} [opts.step] Step for counts and event flow, where a sloped
 *   line between two integers draws values that never happened. Apex's smooth
 *   curve on an 0/1/2 alert count produced sine waves and dipped below zero.
 * @param {boolean} [opts.area] Adds a 12%-to-2% wash under the line. One
 *   series only - washes over each other muddy the identity channel.
 */
export function lineOptions({ step = false, area = false } = {}) {
    const base = baseOptions();

    return {
        ...base,
        chart: { ...base.chart, type: area ? 'area' : 'line' },
        stroke: { curve: step ? 'stepline' : 'smooth', width: 2, lineCap: 'round' },
        // Invisible until hovered, then big enough to hit: a permanent marker
        // on every point of a 30-day series is just texture.
        markers: { size: 0, strokeColors: SURFACE, strokeWidth: 2, hover: { size: 6 } },
        ...(area
            ? {
                  fill: {
                      type: 'gradient',
                      gradient: { shadeIntensity: 0, opacityFrom: 0.12, opacityTo: 0.02, stops: [0, 100] },
                  },
              }
            : {}),
        tooltip: { ...base.tooltip, x: { show: true } },
    };
}

/**
 * Ranges come from the validated ramp rather than apex's auto-shading, which
 * re-derives colours per render. Bin edges are rounded and named, because the
 * legend was printing the raw arithmetic - "0 - 0.3116666666666667".
 */
export function heatmapOptions(colorScaleMax = 10, { formatter = formatNumber } = {}) {
    const base = baseOptions();
    const step = Math.max(colorScaleMax, 0.000001) / RAMP.length;
    // Bin edges are rounded to whatever the step can carry - whole units for a
    // count matrix, a decimal or two for an average-per-week one. Forcing
    // integer edges on a matrix topping out at 1.9 would leave five of the six
    // bands unused.
    const decimals = step >= 1 ? 0 : step >= 0.1 ? 1 : 2;
    const round = (value) => Number(value.toFixed(decimals));
    const edges = RAMP.map((color, i) => ({
        color,
        from: round(i * step),
        to: round((i + 1) * step),
    }));

    return {
        ...base,
        chart: { ...base.chart, type: 'heatmap' },
        plotOptions: {
            heatmap: {
                shadeIntensity: 0,
                radius: 2,
                enableShades: false,
                colorScale: {
                    ranges: edges.map((range, i) => ({
                        // Half-open bins: the previous band's ceiling is this
                        // one's floor, so no value falls between two bands.
                        from: i === 0 ? 0 : range.from + 0.000001,
                        to: range.to,
                        color: range.color,
                        name:
                            i === 0
                                ? `≤ ${formatter(range.to)}`
                                : `${formatter(range.from)}–${formatter(range.to)}`,
                    })),
                },
            },
        },
        fill: { opacity: 1 },
        stroke: { colors: [SURFACE], width: 2 },
        tooltip: { ...base.tooltip, shared: false, intersect: true, y: { formatter } },
    };
}

/**
 * Tiny tile sparkline: no axes, no grid, one quiet line over its own wash,
 * with the current point marked. `lastIndex` is the final data index - the dot
 * is what turns a sparkline from decoration into "here is where we are now".
 */
export function sparkOptions(color = SERIES[0], lastIndex = -1) {
    return {
        chart: {
            type: 'area',
            fontFamily: FONT_STACK,
            sparkline: { enabled: true },
            animations: { enabled: false },
        },
        colors: [color],
        stroke: { curve: 'smooth', width: 2, lineCap: 'round' },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 0, opacityFrom: 0.18, opacityTo: 0.01, stops: [0, 100] },
        },
        markers:
            lastIndex >= 0
                ? {
                      size: 0,
                      hover: { size: 0 },
                      discrete: [
                          {
                              seriesIndex: 0,
                              dataPointIndex: lastIndex,
                              size: 3.5,
                              fillColor: color,
                              // The 2px surface ring, so the dot stays legible
                              // where it sits on the line.
                              strokeColor: SURFACE,
                              strokeWidth: 2,
                          },
                      ],
                  }
                : { size: 0 },
        tooltip: { enabled: false },
    };
}
